import { Workflow, Play } from "lucide-react";
import { Card } from "@/components/ui/card";

interface RegisteredWorkflowEntry {
  name?: string;
  filename?: string;
  description?: string;
  default_inputs?: Record<string, string>;
  expected_outputs?: string[];
}

interface WorkflowListData {
  workflows?: RegisteredWorkflowEntry[];
}

interface WorkflowListWidgetProps {
  data: WorkflowListData;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

export function WorkflowListWidget({ data, onAction }: WorkflowListWidgetProps) {
  const workflows = data?.workflows ?? [];

  if (workflows.length === 0) {
    return (
      <Card variant="default" padding="sm" className="max-w-md animate-slide-up">
        <p className="text-sm text-daydra-dim">No workflows registered.</p>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="sm" className="max-w-2xl animate-slide-up">
      <p className="text-2xs text-daydra-muted font-mono uppercase tracking-wide mb-2">
        registered workflows
      </p>
      <ul className="space-y-2">
        {workflows.map((wf) => (
          <li key={wf.name} className="border-t border-white/[0.06] pt-2 first:border-0 first:pt-0">
            <div className="flex items-center gap-2">
              <Workflow size={11} className="text-daydra-muted shrink-0" />
              <span className="text-sm font-mono text-daydra-text">{wf.name}</span>
              {onAction && wf.name && (
                <button
                  type="button"
                  onClick={() => onAction!(`/run-workflow ${wf.name}`)}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-2xs font-mono text-daydra-muted hover:text-daydra-text hover:bg-white/[0.04] transition-colors"
                  aria-label={`Run ${wf.name}`}
                >
                  <Play size={10} />
                  <span>Run</span>
                </button>
              )}
            </div>
            {wf.description && (
              <p className="text-xs text-daydra-dim mt-0.5 ml-5">{wf.description}</p>
            )}
            <p className="text-2xs text-daydra-muted font-mono mt-1 ml-5">
              <span className="text-daydra-dim">/run-workflow </span>
              {wf.name}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
